import { OrgAssignmentsView, OrgLocationsView } from '@today/ui-domain'
import { useTranslation } from 'react-i18next'
import type { OrganizationCapabilities } from '@/auth/organization-capabilities'
import { useOrgAssignments } from '@/hooks/organization/useOrgAssignments'
import { useOrgLocations } from '@/hooks/organization/useOrgLocations'
import { ORGANIZATION_NAMESPACE } from '@/i18n'
import type { OrgUnitDTO } from '@/services/organization.service'

/** A unit's Locations panel (REQ-007). */
export function LocationsPanel({ unit, canWrite }: { unit: OrgUnitDTO; canWrite: boolean }) {
	const { t } = useTranslation(ORGANIZATION_NAMESPACE)
	const loc = useOrgLocations({ unitId: unit.id, t })
	return (
		<OrgLocationsView
			t={t}
			state={loc.state}
			rows={loc.rows}
			editor={loc.editor}
			deleting={loc.deleting}
			isBusy={loc.isBusy}
			onRetry={loc.retry}
			onEditorChange={loc.change}
			onEditorSubmit={loc.submit}
			onEditorClose={loc.close}
			onConfirmDelete={loc.confirmDelete}
			onCancelDelete={loc.cancelDelete}
			onAdd={canWrite ? loc.openAdd : undefined}
			onEdit={canWrite ? loc.openEdit : undefined}
			onDelete={canWrite ? loc.askDelete : undefined}
		/>
	)
}

/** A unit's Assigned users panel (REQ-008). */
export function AssignmentsPanel({
	unit,
	caps,
	slug,
}: {
	unit: OrgUnitDTO
	caps: OrganizationCapabilities
	slug: string
}) {
	const { t, i18n } = useTranslation(ORGANIZATION_NAMESPACE)
	const a = useOrgAssignments({
		unitId: unit.id,
		unitCode: unit.code,
		slug,
		userDirectory: caps.userDirectory,
		t,
		locale: i18n.language,
	})
	const canWrite = caps.write.assignments
	return (
		<OrgAssignmentsView
			t={t}
			state={a.state}
			stateFilter={a.stateFilter}
			rows={a.rows}
			editor={a.editor}
			userOptions={a.userOptions}
			ending={a.ending}
			rowError={a.rowError}
			isBusy={a.isBusy}
			onStateFilterChange={a.setStateFilter}
			onRetry={a.retry}
			onEditorChange={a.change}
			onEditorSubmit={a.submit}
			onEditorClose={a.close}
			onConfirmEnd={a.confirmEnd}
			onCancelEnd={a.cancelEnd}
			onAssign={canWrite ? a.openAssign : undefined}
			onEnd={canWrite ? a.askEnd : undefined}
			onMakePrimary={canWrite ? a.makePrimary : undefined}
		/>
	)
}
